/**
 * When the athlete's money arrives: one dated list of what is coming and what
 * has come, from contracts and deals, with nothing guessed.
 *
 *   Paychecks. A playing contract's salary is paid in equal installments over
 *   the payout period (contract paragraph 6: 34 or 36 weeks, weekly or
 *   biweekly), starting with the club's first regular-season game. With the
 *   schedule the agency confirmed (contracts.paycheck_weeks/_cadence) and that
 *   game's date (calendar_events, season_type 2), each check lands the week of
 *   first game + k weeks. The amount is the season's salary / the period
 *   (twice that for a biweekly check), at the rate for where the athlete
 *   stood that week (active: full salary; injured reserve or PUP: the split
 *   salary when the contract has one; the practice squad: the squad rate;
 *   released or suspended: nothing). The contract's roster history says where
 *   they stood; today's roster status stands in for the weeks it does not
 *   cover, and a past check priced that way is marked as a projection.
 *
 *   Practice-squad pay. A practice-squad week is not paid under paragraph 6
 *   at all: the player earns a weekly rate for each regular-season week on
 *   the squad, 18 of them from the first game, and no payout period applies.
 *   The rate comes from the contract when the contract records a genuine
 *   practice-squad figure (base salary / 18 landing inside that season's CBA
 *   band, which catches the negotiated veteran rates), and otherwise from the
 *   CBA schedule in payModel for that season and the player's experience. A
 *   full NFL salary on a player who happens to be on the squad is not
 *   mistaken for practice-squad pay, and a season with no rate on file is a
 *   gap rather than a guess. Elevations pay the active check, which cannot be
 *   known in advance, so the projection says what the squad rate is.
 *
 *   Deal payments. Each deal's payment_schedule item is one entry: received
 *   when the agency marked it paid, overdue when its date has passed, due now
 *   within a week, otherwise upcoming. The amount is shown only when the
 *   agency shares the deal's value with the athlete.
 *
 *   Received. Income the agency recorded (athlete_income transactions), so
 *   the list says what actually arrived, not only what should have.
 *
 *   The fee. The agency's own view passes each contract's and deal's
 *   commission rate and gets its share of every item beside it. The portal
 *   route passes no rate, so `fee` is null there and the athlete's page never
 *   carries the agency's cut — nor the amount behind it on an unshared deal.
 *
 * What cannot be projected is returned as a gap with the reason (no confirmed
 * schedule, no first-game date, no salary for the season), for the agency to
 * fill and for the athlete to see as "not known yet" rather than as nothing.
 *
 * Pure; the portal route and the agency's view both use it.
 */

import { practiceSquadWeekly, PRACTICE_SQUAD_WEEKLY, GAME_CHECKS_PER_SEASON } from './payModel.js'
import type { StandingPeriod } from './standingRules.js'

export interface CalendarContractYear {
  year: number
  base_salary?: number | null
  base_salary_split_down?: number | null
}

export interface CalendarContract {
  id: string
  title: string | null
  yearly_breakdown: CalendarContractYear[] | null
  paycheck_weeks: number | null
  paycheck_cadence: 'weekly' | 'biweekly' | null
  /** The agency's percentage, for the fee on each item. The portal never passes it. */
  commission_rate?: number | null
  /** More than two makes a practice-squad player a veteran, on the higher rate. */
  accrued_seasons?: number | null
  /** Where the athlete stood, dated (the pay strip). Decides each past week's rate. */
  roster_history?: StandingPeriod[] | null
}

export interface CalendarDealPayment {
  date: string
  amount: number
  description?: string | null
  status?: string | null
  actual_date?: string | null
}

export interface CalendarDeal {
  id: string
  title: string | null
  payment_schedule: CalendarDealPayment[] | null
  /** The agency shares this deal's value with the athlete. */
  amount_shared: boolean
  commission_rate?: number | null
}

export interface CalendarReceived {
  id: string
  date: string
  amount: number
  description: string | null
  deal_id?: string | null
  contract_id?: string | null
  /** Recorded by Reppr because the roster history had the athlete on a paying roster that week (transactions.status "assumed"). */
  assumed?: boolean
}

export type MoneyStatus = 'received' | 'overdue' | 'due_now' | 'upcoming'

export interface MoneyItem {
  /** YYYY-MM-DD. For a paycheck, the start of the week it lands in. */
  date: string
  kind: 'paycheck' | 'deal_payment' | 'received'
  source_id: string
  source_title: string | null
  /** Null when the amount is not shared or not known. */
  amount: number | null
  status: MoneyStatus
  detail: string
  /** The agency's fee on this item, when a commission rate is on file and the amount is known. */
  fee?: number | null
  /**
   * Paychecks only: the date has passed and the contract's roster history
   * says where the athlete stood that week, so the amount is what that week
   * paid rather than a projection from today's status. Only these may be
   * taken as paid without someone saying so.
   */
  standing_known?: boolean
  /** Received only: Reppr recorded it from the roster rather than a person confirming it. */
  assumed?: boolean
}

/**
 * Why something could not be projected. `kind` is what a screen acts on — the
 * agency's view offers to confirm a paycheck schedule in place — and `reason`
 * is what a person reads. Matching on the prose would break the moment the
 * wording improved.
 */
export type MoneyGapKind = 'paycheck_schedule' | 'roster_status' | 'first_game' | 'no_salary' | 'no_ps_rate' | 'deal_schedule'

export interface MoneyGap {
  source_id: string
  source_title: string | null
  reason: string
  kind: MoneyGapKind
}

export interface MoneyCalendar {
  items: MoneyItem[]
  gaps: MoneyGap[]
  /** Known amounts expected in the next 30 days (not received yet). */
  next_30_days: number
  /** Known amounts received in the last 30 days. */
  last_30_days: number
  /** The part of last_30_days Reppr assumed from the roster and nobody has confirmed. */
  last_30_days_unconfirmed: number
}

const DAY = 24 * 60 * 60 * 1000

const iso = (d: Date) => d.toISOString().slice(0, 10)

/** The agency's share of an amount, or null when either side is unknown. */
function feeOn(amount: number | null, rate: number | null | undefined): number | null {
  if (amount == null || rate == null || !Number.isFinite(Number(rate))) return null
  return (amount * Number(rate)) / 100
}

function dayOf(s: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(s ?? ''))
  if (!m) return null
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])))
  return Number.isNaN(d.getTime()) ? null : d
}

/**
 * Roster statuses whose pay this can price: active, down (split) on IR/PUP,
 * or the practice squad, which is paid by the week and not by paragraph 6.
 */
function payingState(status: string | null | undefined): 'active' | 'down' | 'practice_squad' | null {
  const s = String(status ?? '').toLowerCase()
  if (s === 'active' || s === 'active_roster') return 'active'
  if (s === 'ir' || s === 'injured_reserve' || s === 'pup') return 'down'
  if (s === 'practice_squad' || s === 'ps') return 'practice_squad'
  return null
}

type WeekState = 'active' | 'down' | 'practice_squad' | 'termination_pay' | 'none'

/**
 * Where the roster history puts the athlete on a given day, or null when no
 * period covers it. Periods can touch at a boundary (a move recorded on the
 * day the last one ended); the one that started later wins. A practice-squad
 * week with an elevation date inside it is marked, because it pays the active
 * check instead of the squad rate.
 */
export function standingAt(history: StandingPeriod[], d: Date): { state: WeekState; elevated: boolean } | null {
  const day = iso(d)
  const weekEnd = iso(new Date(d.getTime() + 7 * DAY))
  const p = history
    .filter(h => String(h.start_date).slice(0, 10) <= day && (h.end_date == null || String(h.end_date).slice(0, 10) >= day))
    .sort((a, b) => String(b.start_date).localeCompare(String(a.start_date)))[0]
  if (!p) return null
  const elevated = p.status === 'practice_squad'
    && (Array.isArray(p.elevations) ? p.elevations : []).some(e => { const x = String(e).slice(0, 10); return x >= day && x < weekEnd })
  switch (p.status) {
    case 'active_roster': return { state: 'active', elevated: false }
    case 'injured_reserve':
    case 'pup': return { state: 'down', elevated: false }
    case 'practice_squad': return { state: 'practice_squad', elevated }
    case 'free_agent': return { state: p.termination_pay === true ? 'termination_pay' : 'none', elevated: false }
    case 'suspended': return { state: 'none', elevated: false }
    default: return null
  }
}

/**
 * The weekly practice-squad rate for a season, and where it came from.
 *
 * The contract wins when its figure is genuinely a practice-squad one — a
 * negotiated veteran rate is real money the CBA minimum would understate. It
 * is only believed inside that season's published band, because a player on
 * the squad may still hold a full NFL contract, and $885,000 / 18 is an
 * active-roster check, not squad pay.
 */
function practiceSquadRate(
  season: number,
  contract: CalendarContract,
  year: CalendarContractYear | undefined,
): { weekly: number; source: string } | null {
  const band = PRACTICE_SQUAD_WEEKLY[season]
  const fromContract = year?.base_salary != null ? year.base_salary / GAME_CHECKS_PER_SEASON : null
  if (band && fromContract != null && fromContract >= band.standard && fromContract <= band.veteranMax) {
    return { weekly: fromContract, source: 'the rate on the contract' }
  }
  const cba = practiceSquadWeekly(season, contract.accrued_seasons)
  if (cba != null) {
    const veteran = (contract.accrued_seasons ?? 0) > 2
    return { weekly: cba, source: `the ${season} CBA ${veteran ? 'veteran minimum' : 'practice-squad rate'}` }
  }
  return null
}

export function buildMoneyCalendar(input: {
  contracts: CalendarContract[]
  /** First regular-season game date per season, YYYY-MM-DD. */
  firstGames: Record<number, string>
  /** The athlete's roster status today (clients.roster_status). */
  rosterStatus: string | null
  deals: CalendarDeal[]
  received: CalendarReceived[]
  today: string
  /** Window: days back and ahead of today. */
  daysBack?: number
  daysAhead?: number
  /**
   * Also project paychecks whose date has already passed, as 'overdue'.
   *
   * Off for the forward-looking screens. Turned on by the missed-payment
   * check and by the job that records checks the roster history vouches for;
   * a past check the history does not cover is priced at today's status and
   * carries standing_known: false, so it is never taken as paid.
   */
  includePastPaychecks?: boolean
}): MoneyCalendar {
  const today = dayOf(input.today)
  if (!today) throw new Error(`buildMoneyCalendar: today "${input.today}" is not a date`)
  const from = new Date(today.getTime() - (input.daysBack ?? 60) * DAY)
  const to = new Date(today.getTime() + (input.daysAhead ?? 180) * DAY)
  const inWindow = (d: Date) => d >= from && d <= to
  const items: MoneyItem[] = []
  const gaps: MoneyGap[] = []
  const current = payingState(input.rosterStatus)

  // ── Paychecks ──
  //
  // Each check is priced from the contract and where the athlete stood THAT
  // week. The contract's roster history (the pay strip) answers for every week
  // it covers; today's roster status stands in only for weeks it does not —
  // the future, and past weeks from before the history begins — and a past
  // check priced that way says so (standing_known: false), because an athlete
  // on the practice squad in September was not paid an active check then.
  for (const c of input.contracts) {
    const history = Array.isArray(c.roster_history) ? c.roster_history.filter(p => p && p.start_date) : []
    const years = (c.yearly_breakdown ?? []).filter(y => {
      const first = input.firstGames[y.year]
      const start = first ? dayOf(first) : new Date(Date.UTC(y.year, 8, 1))
      // A season whose payout period could still overlap the window.
      return start != null && start.getTime() <= to.getTime() && start.getTime() + 40 * 7 * DAY >= today.getTime()
    })
    if (years.length === 0) continue
    if (current == null) {
      gaps.push({ source_id: c.id, source_title: c.title, kind: 'roster_status', reason: 'Paychecks depend on roster status; they are shown while on the active roster, injured reserve or the practice squad.' })
      // With no history either, there is no week whose standing is known.
      if (history.length === 0) continue
    }

    const hasSchedule = !!(c.paycheck_weeks && c.paycheck_cadence)
    const step = c.paycheck_cadence === 'biweekly' ? 2 : 1
    const once = new Set<MoneyGapKind>()
    const gapOnce = (kind: MoneyGapKind, reason: string) => {
      if (once.has(kind)) return
      once.add(kind)
      gaps.push({ source_id: c.id, source_title: c.title, kind, reason })
    }

    for (const y of years) {
      const first = input.firstGames[y.year] ? dayOf(input.firstGames[y.year]) : null
      if (!first) {
        gaps.push({ source_id: c.id, source_title: c.title, kind: 'first_game', reason: `The ${y.year} regular-season schedule is not in the calendar yet, so paycheck dates are not known.` })
        continue
      }
      const weeks = Math.max(GAME_CHECKS_PER_SEASON, hasSchedule ? Number(c.paycheck_weeks) : 0)
      for (let week = 0; week < weeks; week++) {
        const d = new Date(first.getTime() + week * 7 * DAY)
        const past = d < today
        if ((past && !input.includePastPaychecks) || !inWindow(d)) continue
        const that = standingAt(history, d)
        const state = that ? that.state : current
        if (state == null || state === 'none') continue
        const known = past && that != null
        const status: MoneyStatus = past ? 'overdue' : d.getTime() - today.getTime() <= 7 * DAY ? 'due_now' : 'upcoming'
        const guessed = past && !known ? ", priced at today's roster status (no record of that week)" : ''

        // The practice squad is paid by the week, so paragraph 6 does not
        // apply and there is no schedule for the agency to confirm.
        if (state === 'practice_squad') {
          if (week >= GAME_CHECKS_PER_SEASON) continue
          const rate = practiceSquadRate(y.year, c, y)
          if (!rate) {
            gapOnce('no_ps_rate', `No practice-squad weekly rate is on file for ${y.year}, so those weeks cannot be priced.`)
            continue
          }
          const elevated = that?.elevated === true
          items.push({
            date: iso(d),
            kind: 'paycheck',
            source_id: c.id,
            source_title: c.title,
            // An elevated week pays the active check, which this cannot price.
            amount: elevated ? null : rate.weekly,
            status,
            detail: elevated
              ? `${y.year} practice squad, week ${week + 1} of ${GAME_CHECKS_PER_SEASON}: elevated to the active roster that week, so the active check replaces the squad rate`
              : `${y.year} practice squad, week ${week + 1} of ${GAME_CHECKS_PER_SEASON}, at ${rate.source}${guessed}`,
            fee: elevated ? null : feeOn(rate.weekly, c.commission_rate),
            standing_known: known && !elevated,
          })
          continue
        }

        // A vested veteran released mid-season keeps base / 18 through week 18.
        if (state === 'termination_pay') {
          if (week >= GAME_CHECKS_PER_SEASON) continue
          if (y.base_salary == null) {
            gapOnce('no_salary', `No ${y.year} salary on the contract.`)
            continue
          }
          const amount = y.base_salary / GAME_CHECKS_PER_SEASON
          items.push({
            date: iso(d), kind: 'paycheck', source_id: c.id, source_title: c.title, amount, status,
            detail: `${y.year} termination pay, week ${week + 1} of ${GAME_CHECKS_PER_SEASON}`,
            fee: feeOn(amount, c.commission_rate),
            standing_known: known,
          })
          continue
        }

        // Active or down (IR, PUP): paragraph 6, over the confirmed schedule.
        if (!hasSchedule) {
          gapOnce('paycheck_schedule', 'The paycheck schedule (34 or 36 weeks, weekly or every two weeks) has not been confirmed by the agency yet.')
          continue
        }
        if (week >= Number(c.paycheck_weeks) || week % step !== 0) continue
        const salary = state === 'down' ? (y.base_salary_split_down ?? y.base_salary ?? null) : (y.base_salary ?? null)
        if (salary == null) {
          gapOnce('no_salary', `No ${y.year} salary on the contract.`)
          continue
        }
        const onSplit = state === 'down' && y.base_salary_split_down != null
        const amount = (salary / Number(c.paycheck_weeks)) * step
        items.push({
          date: iso(d),
          kind: 'paycheck',
          source_id: c.id,
          source_title: c.title,
          amount,
          status,
          detail: `${y.year} salary, check ${week / step + 1} of ${Number(c.paycheck_weeks) / step}${onSplit ? ', at the split (injured reserve) salary' : ''}${guessed}`,
          fee: feeOn(amount, c.commission_rate),
          standing_known: known,
        })
      }
    }
  }

  // ── Deal payments ──
  const paidDealAmounts = new Map<string, number[]>()
  for (const deal of input.deals) {
    const schedule = Array.isArray(deal.payment_schedule) ? deal.payment_schedule : []
    if (schedule.length === 0) {
      gaps.push({ source_id: deal.id, source_title: deal.title, kind: 'deal_schedule', reason: 'No payment schedule on this deal yet.' })
      continue
    }
    for (const p of schedule) {
      const paid = p.status === 'paid'
      const when = dayOf(paid && p.actual_date ? p.actual_date : p.date)
      if (!when || !inWindow(when)) continue
      if (paid) paidDealAmounts.set(deal.id, [...(paidDealAmounts.get(deal.id) ?? []), Number(p.amount)])
      const status: MoneyStatus = paid ? 'received'
        : when < today ? 'overdue'
        : when.getTime() - today.getTime() <= 7 * DAY ? 'due_now'
        : 'upcoming'
      items.push({
        date: iso(when),
        kind: 'deal_payment',
        source_id: deal.id,
        source_title: deal.title,
        amount: deal.amount_shared ? Number(p.amount) : null,
        status,
        detail: [p.description, deal.amount_shared ? null : 'amount not shared'].filter(Boolean).join(' · '),
        fee: feeOn(Number(p.amount), deal.commission_rate),
      })
    }
  }

  // ── Received ──
  for (const r of input.received) {
    const when = dayOf(r.date)
    if (!when || !inWindow(when)) continue
    // A deal payment marked paid on the deal is already listed as received.
    const dup = r.deal_id ? paidDealAmounts.get(r.deal_id) : undefined
    if (dup) {
      const i = dup.indexOf(Number(r.amount))
      if (i >= 0) { dup.splice(i, 1); continue }
    }
    items.push({
      date: iso(when), kind: 'received', source_id: r.id, source_title: r.description, amount: Number(r.amount), status: 'received',
      detail: r.assumed ? 'Assumed from the roster that week' : '',
      ...(r.assumed ? { assumed: true } : {}),
    })
  }

  items.sort((a, b) => a.date.localeCompare(b.date) || a.kind.localeCompare(b.kind))
  const t = today.getTime()
  const next_30_days = items
    .filter(i => i.status !== 'received' && i.amount != null && dayOf(i.date)!.getTime() - t <= 30 * DAY)
    .reduce((s, i) => s + (i.amount as number), 0)
  const lastMonth = items
    .filter(i => i.status === 'received' && i.amount != null && t - dayOf(i.date)!.getTime() <= 30 * DAY && dayOf(i.date)!.getTime() <= t)
  const last_30_days = lastMonth.reduce((s, i) => s + (i.amount as number), 0)
  const last_30_days_unconfirmed = lastMonth.filter(i => i.assumed).reduce((s, i) => s + (i.amount as number), 0)
  return { items, gaps, next_30_days, last_30_days, last_30_days_unconfirmed }
}
